import React, {useEffect, useMemo} from 'react';
import {View, Text, ScrollView, Pressable, ActivityIndicator, DeviceEventEmitter, TouchableOpacity, Platform} from 'react-native';
import {useTheme} from '~contexts/ThemeContext';
import {spacing, borderRadius, typography} from '~constants/design';
import {haptics} from '~Utils/haptics';
import {useTranslation} from 'react-i18next';
import {MapIcon, RefreshIcon, SettingsEditPreferences, ShareIcon} from '~assets/icons';
import {runOnUIThread} from '~Utils/uiThreadSafety';
import { useMapsContext } from '~providers/maps/zustand';
import { useUserStore } from '~providers/userStore/zustand';

interface QuickFiltersProps {
  selectedTimeframe: any;
  onTimeframeSelect: (timeframe: any) => void;
  isMapView: boolean;
  setIsMapView: (value: boolean) => void;
  onServiceTypeChange?: (serviceType: 'THE_REST_TABLE' | 'CULTURAL_EDIT' | null) => void;
  selectedServiceType?: 'THE_REST_TABLE' | 'CULTURAL_EDIT' | null;
}

const QuickFilters: React.FC<QuickFiltersProps> = ({selectedTimeframe, onTimeframeSelect, isMapView, setIsMapView, onServiceTypeChange, selectedServiceType}) => {
  const {colors} = useTheme();
  const {t} = useTranslation();
  const {currentPositionState} = useMapsContext();
  const user = useUserStore(state => state.user);

  // All available quick filters
  const allQuickFilters = [
    /* {id: 1, title: t('home.upcoming_events'), key: 'upcoming_events', value: 'Upcoming Events'},
    {id: 2, title: t('events.today'), key: 'today', value: 'Today'},
    {id: 3, title: t('events.tomorrow'), key: 'tomorrow', value: 'Tomorrow'},
    {id: 5, title: t('events.weekend'), key: 'weekend', value: 'This Weekend'}, */
    {
      id: 9,
      title: t('events.pyxi_select'),
      key: 'pyxi_select',
      value: 'Pyxi Select',
      showInUK: true,
      showOutsideUK: false,
    },
    {
      id: 8,
      title: t('events.neighbourhood_gropus'),
      key: 'neighbourhood_gropus',
      value: 'Neighbourhoods',
      showInUK: true,
      showOutsideUK: false,
    },
    {id: 10, title: t('events.pyxi_core'), key: 'pyxi_core', value: 'PyXi Core', showInUK: false, showOutsideUK: true},
  ];
  
  // Initialize isUK based on currentPositionState if available
  const [isUK, setIsUK] = React.useState(() => {
    if (currentPositionState?.country) {
      return currentPositionState.country === 'United Kingdom';
    }
    return true; // Default to UK
  });
  
  // Sort filters: enabled ones first, disabled ones last
  const quickFilters = useMemo(() => {
    return allQuickFilters.sort((a, b) => {
      const aEnabled = isUK ? a.showInUK : a.showOutsideUK;
      const bEnabled = isUK ? b.showInUK : b.showOutsideUK;
      
      // If both enabled or both disabled, maintain original order
      if (aEnabled === bEnabled) return 0;
      // Enabled filters come first (return negative for a if a is enabled)
      return aEnabled ? -1 : 1;
    });
  }, [isUK]);

  useEffect(() => {
    if (currentPositionState?.country) {
        const {country} = currentPositionState;
        const ukLocation = country === 'United Kingdom';
        setIsUK(ukLocation);
    }
  }, [currentPositionState])

  useEffect(() => {
    const eventListener = DeviceEventEmitter.addListener('homeLocationChange', data => {
      runOnUIThread(() => {
        const {country} = data;
        if (!country) {
          return;
        }
        
        const ukLocation = country === 'United Kingdom';
        setIsUK(ukLocation);
        
        // Handle automatic filter switching based on location changes
        const pyxiSelectId = 9;
        const pyxiCoreId = 10;
        
        // Get the current selected filter ID (avoid using selectedTimeframe from closure)
        const currentFilterId = selectedTimeframe?.id;
        
        // If user is currently on Pyxi Select but moves outside UK, switch to Pyxi Core
        if (currentFilterId === pyxiSelectId && !ukLocation) {
          const pyxiCoreFilter = allQuickFilters.find(f => f.id === pyxiCoreId);
          if (pyxiCoreFilter) {
            onTimeframeSelect(pyxiCoreFilter);
            if (onServiceTypeChange) {
              onServiceTypeChange(null); // Clear service type when switching to Pyxi Core
            }
          }
        }
        // If user is currently on Pyxi Core but moves to UK, switch to Pyxi Select
        else if (currentFilterId === pyxiCoreId && ukLocation) {
          const pyxiSelectFilter = allQuickFilters.find(f => f.id === pyxiSelectId);
          if (pyxiSelectFilter) {
            onTimeframeSelect(pyxiSelectFilter);
            if (onServiceTypeChange) {
              onServiceTypeChange('THE_REST_TABLE'); // Default to THE_REST_TABLE when switching to Pyxi Select
            }
          }
        }
      });
    });

    return () => {
      eventListener.remove();
    };
  }, [selectedTimeframe?.id, onTimeframeSelect, onServiceTypeChange]);

  const handleFilterPress = (filter: any) => {
    // Check if filter should be enabled based on location
    const isFilterEnabled = isUK ? filter.showInUK : filter.showOutsideUK;
    if (!isFilterEnabled) {
      return; // Don't handle press for disabled filters
    }
    
    haptics.light();
    
    // Handle Pyxi Select and Pyxi Core mutual exclusion
    const pyxiSelectId = 9;
    const pyxiCoreId = 10;
    
    // If selecting Pyxi Select (id 9), deselect Pyxi Core (id 10)
    if (filter.id === pyxiSelectId && selectedTimeframe?.id === pyxiCoreId) {
      onTimeframeSelect(filter);
      if (onServiceTypeChange) {
        onServiceTypeChange('THE_REST_TABLE'); // Default to THE_REST_TABLE when Pyxi Select is selected
      }
    }
    // If selecting Pyxi Core (id 10), deselect Pyxi Select (id 9)
    else if (filter.id === pyxiCoreId && selectedTimeframe?.id === pyxiSelectId) {
      onTimeframeSelect(filter);
      if (onServiceTypeChange) {
        onServiceTypeChange(null); // Clear service type when Pyxi Core is selected
      }
    }
    // Otherwise, apply filter directly
    else {
      onTimeframeSelect(filter);
    }
  };

  const FilterChip = ({filter}: {filter: any}) => {
    const isSelected = selectedTimeframe?.title === filter.title;
    const isFilterEnabled = isUK ? filter.showInUK : filter.showOutsideUK;
    
    // A filter is disabled only if it's not available in the current location
    // Don't apply mutual exclusion logic - location availability is the only rule
    const isFilterDisabled = !isFilterEnabled;

    return (
      <Pressable
        onPress={() => handleFilterPress(filter)}
        disabled={isFilterDisabled}
        style={({pressed}) => ({
          paddingHorizontal: spacing.sm,
          marginHorizontal: spacing.xs,
          paddingVertical: spacing.sm,
          borderRadius: borderRadius.lg,
          backgroundColor: isSelected ? colors.primary : colors.surface,
          borderWidth: 0,
          minHeight: 44,
          justifyContent: 'center',
          alignItems: 'center',
          opacity: isFilterDisabled ? 0.5 : 1, // Gray out disabled filters
          transform: [{scale: pressed && !isFilterDisabled ? 0.97 : 1}],
          // Add subtle shadow for selected state
          shadowColor: isSelected ? colors.primary : 'transparent',
          shadowOffset: {
            width: 0,
            height: 2,
          },
          shadowOpacity: isSelected ? 0.3 : 0,
          shadowRadius: 6,
          elevation: isSelected ? 4 : 0,
          // Make selected tab slightly larger
          minWidth: isSelected ? 120 : 105,
        })}>
        <Text
          style={{
            fontSize: typography.fontSize.sm,
            fontWeight: isSelected ? typography.fontWeight.bold : typography.fontWeight.semibold,
            color: isFilterDisabled 
              ? colors.textSecondary
              : (isSelected ? colors.white : colors.textPrimary),
            textAlign: 'center',
          }}>
          {filter.title}
        </Text>
      </Pressable>
    );
  };

  // View Toggle Component
  const ViewToggle = () => (
    <View
      style={{
        flexDirection: 'row',
        backgroundColor: colors.surface,
        borderRadius: borderRadius.full,
        padding: 3,
        borderWidth: 1,
        borderColor: colors.border,
        height: 40,
        width: 80,
        marginLeft: spacing.sm,
        // Left shadow on the entire toggle
        shadowColor: '#000',
        shadowOffset: {
          width: -3,
          height: 0,
        },
        shadowOpacity: 0.15,
        shadowRadius: 3,
        elevation: 3,
      }}>
      <Pressable
        onPress={() => setIsMapView(false)}
        style={({pressed}) => ({
          flexDirection: 'row',
          alignItems: 'center',
          paddingHorizontal: spacing.xs,
          borderRadius: borderRadius.full,
          backgroundColor: !isMapView ? colors.textPrimary : 'transparent',
          flex: 1,
          justifyContent: 'center',
          transform: [{scale: pressed ? 0.95 : 1}],
        })}>
        {/* List Icon */}
        <View style={{alignItems: 'center', justifyContent: 'center'}}>
          <View
            style={{
              width: 16,
              height: 2.5,
              backgroundColor: !isMapView ? colors.white : colors.textSecondary,
              marginBottom: 3,
              borderRadius: 1,
            }}
          />
          <View
            style={{
              width: 16,
              height: 2.5,
              backgroundColor: !isMapView ? colors.white : colors.textSecondary,
              marginBottom: 3,
              borderRadius: 1,
            }}
          />
          <View
            style={{
              width: 16,
              height: 2.5,
              backgroundColor: !isMapView ? colors.white : colors.textSecondary,
              borderRadius: 1,
            }}
          />
        </View>
      </Pressable>

      <Pressable
        onPress={() => setIsMapView(true)}
        style={({pressed}) => ({
          flexDirection: 'row',
          alignItems: 'center',
          paddingHorizontal: spacing.xs,
          borderRadius: borderRadius.full,
          backgroundColor: isMapView ? colors.textPrimary : 'transparent',
          flex: 1,
          justifyContent: 'center',
          transform: [{scale: pressed ? 0.95 : 1}],
        })}>
        {/* Map Icon */}
        <MapIcon color={isMapView ? colors.white : colors.textSecondary} size={20} />
      </Pressable>
    </View>
  );

  // Additional options that appear when Pyxi Select is selected
  const PyxiSelectOptions = () => {
    const isPyxiSelectSelected = selectedTimeframe?.id === 9; // Pyxi Select id is 9
    
    if (!isPyxiSelectSelected) {
      return null;
    }

    const options = [
      { 
        id: 'reset', 
        title: '✨ Reset Table',
        serviceType: 'THE_REST_TABLE' as const
      },
      { 
        id: 'culture', 
        title: '🎭 Culture Edit',
        serviceType: 'CULTURAL_EDIT' as const
      },
      { 
        id: 'ritual', 
        title: 'Shared Ritual (Coming soon)',
        serviceType: null
      },
    ];

    return (
      <View style={{
        paddingHorizontal: spacing.md,
        paddingTop: spacing.sm,
        paddingBottom: spacing.md,
      }}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{
            alignItems: 'center',
            justifyContent: 'center',
            flexGrow: 1,
          }}>
        {options.map((option, index) => (
            <Pressable
              key={option.id}
              disabled={option.serviceType === null} // Disable "Shared Ritual" option
              onPress={() => {
                haptics.light();
                // Handle option selection
                console.log('Selected option:', option.title, 'Service Type:', option.serviceType);
                console.log('Previous selectedServiceType:', selectedServiceType);
                if (onServiceTypeChange) {
                  onServiceTypeChange(option.serviceType);
                  console.log('Called onServiceTypeChange with:', option.serviceType);
                }
              }}
              style={({pressed}) => ({
                paddingHorizontal: spacing.lg,
                paddingVertical: spacing.sm,
                borderRadius: borderRadius.lg,
                backgroundColor: selectedServiceType === option.serviceType ? colors.primary : colors.white,
                marginHorizontal: spacing.xs,
                minHeight: 40,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                transform: [{scale: pressed ? 0.97 : 1}],
                // Add subtle shadow for selected state
                shadowColor: selectedServiceType === option.serviceType ? colors.primary : 'transparent',
                shadowOffset: {
                  width: 0,
                  height: 2,
                },
                shadowOpacity: selectedServiceType === option.serviceType ? 0.3 : 0,
                shadowRadius: 4,
                elevation: selectedServiceType === option.serviceType ? 3 : 0,
                // Make "Shared Ritual" appear disabled
                opacity: option.serviceType === null ? 0.6 : 1,
              })}>
              <Text
                style={{
                  fontSize: typography.fontSize.sm,
                  fontWeight: selectedServiceType === option.serviceType ? typography.fontWeight.bold : typography.fontWeight.medium,
                  color: option.serviceType === null 
                    ? colors.textSecondary // Disabled color for "Shared Ritual"
                    : (selectedServiceType === option.serviceType ? colors.white : colors.textPrimary),
                  textAlign: 'center',
                }}>
                {option.title}
              </Text>
            </Pressable>
          ))}
        </ScrollView>
      </View>
    );
  };

  // Welcome Section Component
  const WelcomeSection = () => {
    const isPyxiSelectSelected = selectedTimeframe?.id === 9; // Pyxi Select id is 9
    
    if (!isPyxiSelectSelected) {
      return null;
    }

    const userName = user?.first_name || 'Friend';

    return (
      <View style={{
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.lg,
      }}>
        <Text style={{
          fontSize: typography.fontSize['2xl'],
          fontWeight: typography.fontWeight.bold,
          color: colors.textPrimary,
          marginBottom: spacing.xs,
        }}>
          Hey {userName}! 👋
        </Text>
        <Text style={{
          fontSize: typography.fontSize.base,
          color: colors.textSecondary,
          fontWeight: typography.fontWeight.medium,
        }}>
          Ready for your next adventure?
        </Text>
      </View>
    );
  };

  // Dynamic Content Section Component
  const DynamicContentSection = () => {
    const isPyxiSelectSelected = selectedTimeframe?.id === 9; // Pyxi Select id is 9
    
    if (!isPyxiSelectSelected || !selectedServiceType) {
      return null;
    }

    // Define content for each service type
    const contentByServiceType = {
      'THE_REST_TABLE': {
        emoji: '✨',
        title: 'Reset Your Social Table',
        description: 'Meaningful conversations over thoughtfully prepared meals. Connect with like-minded people and reset your dining experience.'
      },
      'CULTURAL_EDIT': {
        emoji: '🎭',
        title: 'Culture Edit',
        description: 'Pyxi’s newest event series designed to bring together a matched group of curious, thoughtful minds for an afternoon of art, culture, and conversation.'
      }
    };

    const currentContent = contentByServiceType[selectedServiceType];

    if (!currentContent) {
      return null;
    }

    return (
      <View style={{
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.lg,
        backgroundColor: colors.background,
      }}>
        {/* Dynamic Header */}
        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          marginBottom: spacing.md,
        }}>
          <Text style={{ fontSize: 24, marginRight: spacing.sm }}>{currentContent.emoji}</Text>
          <Text style={{
            fontSize: typography.fontSize.xl,
            fontWeight: typography.fontWeight.bold,
            color: colors.textPrimary,
          }}>
            {currentContent.title}
          </Text>
        </View>

        {/* Dynamic Description */}
        <Text style={{
          fontSize: typography.fontSize.base,
          color: colors.textSecondary,
          lineHeight: 24,
          marginBottom: spacing.lg,
        }}>
          {currentContent.description}
        </Text>
      </View>
    );
  };

  useEffect(() => {
    if (selectedTimeframe?.id !== 10) {
      setIsMapView(false);
    }
  }, [selectedTimeframe?.id])

  return (
    <TouchableOpacity 
      activeOpacity={1}
      style={{
        paddingVertical: spacing.md,
        backgroundColor: colors.background,
      }}>
      {/* Filters row with optional ViewToggle on the right when Pyxi Select is selected */}
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={{ flex: 1 }}
          contentContainerStyle={{
            flexGrow: 1,
            paddingRight: spacing.sm,
          }}>
          {quickFilters.map(filter => (
            <FilterChip key={filter.id} filter={filter} />
          ))}
        </ScrollView>
        {/* Show toggle only when Pyxi Select is active */}
        {Platform.OS !== 'web' && selectedTimeframe?.id === 10 ? <ViewToggle /> : null}
      </View>
      
      {/* Welcome Section */}
      <WelcomeSection />
      
      {/* Additional options for Pyxi Select */}
      <PyxiSelectOptions />
      
      {/* Dynamic Content Section */}
      <DynamicContentSection />
    </TouchableOpacity>
  );
};

export default QuickFilters;
